import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Columns3 } from "lucide-react";
import { toast } from "sonner";

const SECTION_TITLES = {
  system: "System Columns",
  capture: "Call Capture",
  poc: "Point of Contact",
  head_office: "Head Office",
};
const SECTION_ORDER = ["system", "capture", "poc", "head_office"];

/** Admin-facing column selector for lead exports. Preferences are stored server
 * side as exclusions, so any newly added field is included by default. */
export const ExportColumnsDialog = ({ triggerClassName = "" }) => {
  const [open, setOpen] = useState(false);
  const [columns, setColumns] = useState([]);
  const [checked, setChecked] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads/export/columns");
      setColumns(data.columns || []);
      setChecked(new Set((data.columns || []).filter((c) => c.checked).map((c) => c.id)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load columns");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = (id) =>
    setChecked((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const setAll = (val) =>
    setChecked(val ? new Set(columns.map((c) => c.id)) : new Set());

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/leads/export/columns", { checked: Array.from(checked) });
      toast.success("Export columns saved");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save columns");
    } finally {
      setSaving(false);
    }
  };

  const grouped = SECTION_ORDER
    .map((s) => ({ section: s, items: columns.filter((c) => c.section === s) }))
    .filter((g) => g.items.length);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className={`h-8 ${triggerClassName}`}
                data-testid="export-columns-btn">
          <Columns3 className="w-3.5 h-3.5 mr-1.5" /> Columns
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="export-columns-dialog">
        <DialogHeader>
          <DialogTitle>Export Columns</DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-between text-xs text-slate-500 -mt-1">
          <span>{checked.size} of {columns.length} selected</span>
          <div className="flex gap-3">
            <button className="hover:text-slate-900" onClick={() => setAll(true)}
                    data-testid="columns-select-all">Select all</button>
            <button className="hover:text-slate-900" onClick={() => setAll(false)}
                    data-testid="columns-clear-all">Clear</button>
          </div>
        </div>
        <ScrollArea className="h-[52vh] pr-3">
          {loading ? (
            <div className="text-sm text-slate-400 py-6 text-center">Loading…</div>
          ) : (
            grouped.map((g) => (
              <div key={g.section} className="mb-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">
                  {SECTION_TITLES[g.section] || g.section}
                </div>
                <div className="space-y-1.5">
                  {g.items.map((c) => (
                    <label key={c.id}
                           className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer py-0.5"
                           data-testid={`column-row-${c.id}`}>
                      <Checkbox checked={checked.has(c.id)} onCheckedChange={() => toggle(c.id)}
                                data-testid={`column-check-${c.id}`} />
                      <span>{c.header}</span>
                      {c.deleted && (
                        <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                          archived field
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ))
          )}
        </ScrollArea>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving}
                  className="bg-indigo-600 hover:bg-indigo-700" data-testid="columns-save-btn">
            {saving ? "Saving…" : "Save preferences"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportColumnsDialog;
